import { useState } from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { Plus, Pencil, Trash2, Gift, ToggleLeft, ToggleRight } from "lucide-react"

import * as api from "@/api/cogeser"
import { useAuth } from "@/hooks/useAuth"
import { usePermissions } from "@/hooks/usePermissions"

// ----------------------------------------------------------------------------

interface IReward {
  id: string
  name: string
  description: string
  pointsCost: number
  category?: string
  imageUrl?: string
  stock: number
  active: boolean
  validFrom?: string
  validUntil?: string
}

export default function Rewards() {
  const { token } = useAuth()
  const { canEditRules } = usePermissions()
  const queryClient = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const [editingReward, setEditingReward] = useState<IReward | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ["rewards"],
    queryFn: () => api.getRewards({ limit: 100 }, token!) as Promise<{ data: IReward[]; count: number }>,
    enabled: !!token,
  })

  const rewards = data?.data || []

  const toggleMutation = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      api.updateReward(id, { active }, token!),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["rewards"] }),
    onError: () => toast.error("Errore nell'aggiornamento"),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteReward(id, token!),
    onSuccess: () => {
      toast.success("Premio disattivato")
      queryClient.invalidateQueries({ queryKey: ["rewards"] })
    },
    onError: () => toast.error("Errore"),
  })

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Catalogo Premi</h1>
          <p className="mt-1 text-sm text-gray-500">Gestione dei premi riscattabili con i punti fidelity</p>
        </div>
        {canEditRules && (
          <button
            onClick={() => { setEditingReward(null); setShowForm(true) }}
            className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="h-4 w-4" /> Nuovo Premio
          </button>
        )}
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50">
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Premio</th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Categoria</th>
              <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Punti</th>
              <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Stock</th>
              <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Validità</th>
              <th className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500">Attivo</th>
              {canEditRules && <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Azioni</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {isLoading ? (
              <tr><td colSpan={7} className="px-6 py-12 text-center text-sm text-gray-500">Caricamento...</td></tr>
            ) : rewards.length === 0 ? (
              <tr><td colSpan={7} className="px-6 py-12 text-center text-sm text-gray-500">Nessun premio nel catalogo</td></tr>
            ) : (
              rewards.map((reward) => (
                <tr key={reward.id} className={`hover:bg-gray-50 ${!reward.active ? "opacity-50" : ""}`}>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                        <Gift className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{reward.name}</p>
                        <p className="text-xs text-gray-500 line-clamp-1">{reward.description}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600">
                      {reward.category || "—"}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <span className="text-sm font-semibold text-primary">{reward.pointsCost}</span>
                  </td>
                  <td className="px-6 py-4 text-right text-sm text-gray-600">
                    {reward.stock < 0 ? "∞" : reward.stock}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {reward.validFrom ? new Date(reward.validFrom).toLocaleDateString("it-IT") : "—"}
                    {" → "}
                    {reward.validUntil ? new Date(reward.validUntil).toLocaleDateString("it-IT") : "—"}
                  </td>
                  <td className="px-6 py-4 text-center">
                    {canEditRules ? (
                      <button
                        onClick={() => toggleMutation.mutate({ id: reward.id, active: !reward.active })}
                        title={reward.active ? "Disattiva" : "Attiva"}
                      >
                        {reward.active ? <ToggleRight className="h-6 w-6 text-primary" /> : <ToggleLeft className="h-6 w-6 text-gray-300" />}
                      </button>
                    ) : (
                      reward.active ? <ToggleRight className="h-6 w-6 text-primary" /> : <ToggleLeft className="h-6 w-6 text-gray-300" />
                    )}
                  </td>
                  {canEditRules && (
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => { setEditingReward(reward); setShowForm(true) }} className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600">
                          <Pencil className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showForm && (
        <RewardFormDialog
          reward={editingReward}
          token={token!}
          onClose={() => { setShowForm(false); setEditingReward(null) }}
          onSuccess={() => { queryClient.invalidateQueries({ queryKey: ["rewards"] }); setShowForm(false); setEditingReward(null) }}
        />
      )}
    </div>
  )
}

// ============================================================================

function RewardFormDialog({ reward, token, onClose, onSuccess }: {
  reward: IReward | null
  token: string
  onClose: () => void
  onSuccess: () => void
}) {
  const isEditing = !!reward
  const [isLoading, setIsLoading] = useState(false)
  const [name, setName] = useState(reward?.name || "")
  const [description, setDescription] = useState(reward?.description || "")
  const [pointsCost, setPointsCost] = useState(String(reward?.pointsCost || ""))
  const [category, setCategory] = useState(reward?.category || "physical")
  const [imageUrl, setImageUrl] = useState(reward?.imageUrl || "")
  const [stock, setStock] = useState(String(reward?.stock ?? -1))
  const [validFrom, setValidFrom] = useState(reward?.validFrom ? reward.validFrom.split("T")[0] : "")
  const [validUntil, setValidUntil] = useState(reward?.validUntil ? reward.validUntil.split("T")[0] : "")

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setIsLoading(true)
    try {
      const body: Record<string, unknown> = {
        name,
        description,
        pointsCost: parseInt(pointsCost) || 0,
        category,
        imageUrl: imageUrl || undefined,
        stock: parseInt(stock),
        validFrom: validFrom || undefined,
        validUntil: validUntil || undefined,
      }
      if (isEditing) {
        await api.updateReward(reward.id, body, token)
        toast.success("Premio aggiornato")
      } else {
        await api.createReward(body, token)
        toast.success("Premio creato")
      }
      onSuccess()
    } catch {
      toast.error(isEditing ? "Errore nell'aggiornamento" : "Errore nella creazione")
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl">
        <h3 className="text-lg font-semibold text-gray-900">{isEditing ? "Modifica Premio" : "Nuovo Premio"}</h3>
        <form onSubmit={handleSubmit} className="mt-4 space-y-4">
          <div className="space-y-1.5">
            <label className="block text-sm font-medium text-gray-700">Nome</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} required className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20" />
          </div>
          <div className="space-y-1.5">
            <label className="block text-sm font-medium text-gray-700">Descrizione</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} required rows={2} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-gray-700">Punti richiesti</label>
              <input type="number" value={pointsCost} onChange={(e) => setPointsCost(e.target.value)} required min={0} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20" />
            </div>
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-gray-700">Stock</label>
              <input type="number" value={stock} onChange={(e) => setStock(e.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20" />
              <p className="text-xs text-gray-400">-1 = illimitato</p>
            </div>
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-gray-700">Categoria</label>
              <select value={category} onChange={(e) => setCategory(e.target.value)} className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20">
                <option value="physical">Premi fisici</option>
                <option value="bill_discount">Sconto bolletta</option>
                <option value="digital">Premi digitali</option>
              </select>
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="block text-sm font-medium text-gray-700">URL immagine (opzionale)</label>
            <input type="url" value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} placeholder="https://..." className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-gray-700">Valido dal</label>
              <input type="date" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20" />
            </div>
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-gray-700">Valido fino al</label>
              <input type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20" />
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Annulla</button>
            <button type="submit" disabled={isLoading} className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
              {isLoading ? "Salvataggio..." : isEditing ? "Salva" : "Crea"}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
